import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Divider,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import theme from '../../theme';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

const CartScreen = () => {
  const [cartData, setCartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate(); // Initialize the navigate hook

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(BASE_URL + '/cart/3d4279fc-b438-4a0b-8e7e-9ec5ceb9deb8', {
          method: 'GET',
          headers: {
            authorization: '7633d6c7-1111-4fd9-b78c-14e0312957f8',
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.status) {
          setCartData(data.data);
        } else {
          setError('Failed to fetch cart data.');
        }
      } catch (error) {
        setError('An error occurred while fetching the data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteItem = (itemId) => {
    const updatedCart = cartData.lab_items.filter((item) => item.reference_id !== itemId);
    setCartData((prevData) => ({
      ...prevData,
      lab_items: updatedCart,
    }));
  };

  const handleCheckout = () => {
    navigate('/booking'); // Navigate to the /booking route
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box p={2} bgcolor="#F9FAFB" minHeight="100vh">
        {/* Lab Details Section */}
        <Box
          display="flex"
          alignItems="center"
          mb={3}
          p={2}
          sx={{ backgroundColor: '#FFFFFF', borderRadius: 2, boxShadow: 1 }}
        >
          <Box
            component="img"
            src="https://via.placeholder.com/50" // Replace with actual lab logo
            alt="Lab Logo"
            sx={{ width: 50, height: 50, borderRadius: '50%' }}
          />
          <Box ml={2}>
            <Typography variant="h6" fontWeight="bold">
              Microtech Diagnostic Lab
            </Typography>
            <Button
              size="small"
              sx={{
                mt: 0.5,
                textTransform: 'none',
                fontSize: '12px',
                backgroundColor: '#EAFCE3',
                color: '#3A9F3B',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#D6F7D3' },
              }}
            >
              View Lab Details
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Cart Items Section */}
          <Grid item xs={12} md={8}>
            {cartData.lab_items.map((item) => (
              <Box
                key={item.reference_id}
                mb={2}
                p={2}
                sx={{
                  borderRadius: 2,
                  boxShadow: 1,
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.description || 'No description available'}
                    </Typography>
                    <Box display="flex" alignItems="center" mt={0.5}>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: '#9E9E9E',
                          marginRight: '8px',
                        }}
                      >
                        ₹{item.amount}
                      </Typography>
                      <Typography variant="body1" color="green" fontWeight="bold">
                        ₹{item.total_price}
                      </Typography>
                    </Box>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      variant="caption"
                      sx={{
                        backgroundColor: '#FFEBEE',
                        color: '#D32F2F',
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        borderRadius: '16px',
                      }}
                    >
                      34% OFF
                    </Typography>
                    <IconButton color="error" onClick={() => handleDeleteItem(item.reference_id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>

          {/* Summary Section */}
          <Grid item xs={12} md={4}>
            <Box
              p={2}
              sx={{
                borderRadius: 2,
                boxShadow: 1,
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  ₹{cartData.amount.toFixed(2)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Discount applied
                </Typography>
                <Typography variant="body2" color="green" fontWeight="bold">
                  -₹{cartData.discount.toFixed(2)}
                </Typography>
              </Box>
              <Divider />
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Typography variant="body1" fontWeight="bold">
                  Total Price
                </Typography>
                <Typography variant="h6" color="green" fontWeight="bold">
                  ₹{cartData.total_price.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              fullWidth
              sx={{
                mt: 3,
                borderRadius: 8,
                padding: '12px 16px',
                fontWeight: 'bold',
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#388E3C' },
              }}
              onClick={handleCheckout} // Trigger navigation
            >
              Proceed to Checkout
            </Button>
          </Grid>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default CartScreen;